import React, { useState } from "react";
import api from "@/lib/api";
import { useLiveQuery, TAGS } from "@/lib/liveSync";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Trash, Plus, Sparkle } from "@phosphor-icons/react";

export default function Plans() {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    name: "", price: 0, duration_days: 30, discount_percent: 100,
    description: "", active: true,
  });

  const { data: plans, refetch } = useLiveQuery(
    TAGS.PLANS,
    async () => (await api.get("/plans")).data,
  );

  const save = async () => {
    try {
      if (editing) {
        await api.put(`/plans/${editing.id}`, {
          ...form, price: Number(form.price),
          duration_days: Number(form.duration_days),
          discount_percent: Number(form.discount_percent),
        });
        toast.success("Plan güncellendi. Diğer sayfalar otomatik yenilenecek.");
      } else {
        await api.post("/plans", {
          ...form, price: Number(form.price),
          duration_days: Number(form.duration_days),
          discount_percent: Number(form.discount_percent),
        });
        toast.success("Plan oluşturuldu");
      }
      setOpen(false); setEditing(null);
      setForm({ name: "", price: 0, duration_days: 30, discount_percent: 100, description: "", active: true });
    } catch (e) { toast.error(e?.response?.data?.detail || "Kaydedilemedi"); }
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name, price: p.price, duration_days: p.duration_days,
      discount_percent: p.discount_percent, description: p.description || "",
      active: !!p.active,
    });
    setOpen(true);
  };

  const del = async (id) => {
    if (!window.confirm("Plan silinsin mi?")) return;
    try { await api.delete(`/plans/${id}`); toast.success("Silindi"); }
    catch (e) { toast.error("Silinemedi"); }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground">Abonelik</div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight">Abonelik Planları</h1>
        </div>
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setEditing(null); }}>
          <DialogTrigger asChild>
            <Button data-testid="plan-add-btn" className="rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-widest font-semibold">
              <Plus size={16} /> Yeni Plan
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{editing ? "Plan Düzenle" : "Yeni Plan"}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Ad</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-sm" data-testid="plan-name-input" /></div>
              <div className="grid grid-cols-3 gap-3">
                <div><Label>Fiyat (₺)</Label><Input type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="rounded-sm" data-testid="plan-price-input" /></div>
                <div><Label>Süre (gün)</Label><Input type="number" value={form.duration_days} onChange={(e) => setForm({ ...form, duration_days: e.target.value })} className="rounded-sm" /></div>
                <div><Label>İndirim (%)</Label><Input type="number" min="0" max="100" value={form.discount_percent} onChange={(e) => setForm({ ...form, discount_percent: e.target.value })} className="rounded-sm" data-testid="plan-discount-input" /></div>
              </div>
              <div><Label>Açıklama</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-sm" /></div>
              <div className="flex items-center gap-2">
                <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} id="plan-active" />
                <Label htmlFor="plan-active">Aktif</Label>
              </div>
              <Button data-testid="plan-save-btn" onClick={save} className="w-full rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-widest font-semibold">
                Kaydet
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {(plans || []).map((p) => (
          <Card key={p.id} className="rounded-sm border-border">
            <CardContent className="p-5 space-y-3">
              <div className="flex items-start justify-between">
                <Sparkle size={22} weight="fill" className="text-primary" />
                {p.active ? <Badge variant="outline" className="rounded-sm text-[10px] uppercase tracking-widest">Aktif</Badge>
                  : <Badge variant="secondary" className="rounded-sm text-[10px] uppercase tracking-widest">Pasif</Badge>}
              </div>
              <div>
                <div className="text-lg font-bold">{p.name}</div>
                <div className="text-xs text-muted-foreground mt-1">{p.description}</div>
              </div>
              <div className="pt-3 border-t border-border">
                <div className="text-3xl font-bold font-mono">₺{p.price}</div>
                <div className="text-xs text-muted-foreground">{p.duration_days} gün · %{p.discount_percent} indirim</div>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => openEdit(p)} className="rounded-sm flex-1" data-testid={`plan-edit-${p.name}`}>Düzenle</Button>
                <Button variant="ghost" size="sm" onClick={() => del(p.id)} data-testid={`plan-del-${p.name}`}><Trash size={16} /></Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
